/** ***********
*
* <group-users-list.component.ts v1.0>
*
* Copyright (c) 2022 AIRBUS OPERATIONS
*
* Contributors:
*  GARY Adrien (NG635E8) [adrien.gary.external@airbus.com]
*  NG6C740 [thomas.pereira.external@airbus.com]
*
* All rights reserved.
**************/

import { SelectionModel } from '@angular/cdk/collections';
import {
    Component,
    ChangeDetectionStrategy,
    Input,
    ViewChild,
    OnChanges,
    SimpleChanges,
    ChangeDetectorRef,
    Output,
    EventEmitter,
    OnDestroy,
    AfterViewInit,
    ElementRef,
} from '@angular/core';
import { MatButton } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { Subscription } from 'rxjs';
import { User } from 'src/app/models/user';
import { SelectUserComponent } from '../select-user/select-user.component';
import { MatTableDataSource } from '@angular/material/table';
import { MatSort } from '@angular/material/sort';
import { MatPaginator } from '@angular/material/paginator';
import { BooleanInput, coerceBooleanProperty } from '@angular/cdk/coercion';

@Component({
    selector: 'app-group-users-list',
    templateUrl: './group-users-list.component.html',
    styleUrls: ['../group-deps-list.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class GroupUsersListComponent implements AfterViewInit, OnChanges, OnDestroy {

    @ViewChild('table') public tableContainer: ElementRef<HTMLDivElement>;
    @ViewChild(MatButton) public addUserButton: MatButton;
    @ViewChild(MatSort, { static: true }) sort: MatSort;
    @ViewChild(MatPaginator, { static: false }) paginator: MatPaginator;

    @Input() public users!: User[] & { removed?:boolean };
    // #region usersLoading
    private _usersLoading = false;
    @Input()
    public set usersLoading(value: BooleanInput) {
        this._usersLoading = coerceBooleanProperty(value);
    }
    public get usersLoading(): boolean {
        return this._usersLoading;
    }
    // #endregion
    @Input() public height = 'auto';
    // #region disabled
    private _disabled = false;
    @Input()
    public set disabled(value: BooleanInput) {
        this._disabled = coerceBooleanProperty(value);
    }
    public get disabled(): boolean {
        return this._disabled;
    }
    // #endregion
    @Output() removeUsers = new EventEmitter<User[]>();
    @Output() addUsers = new EventEmitter<User[]>();
    @Output() pageChange = new EventEmitter<void>();
    public dataSource = new MatTableDataSource<User>();
    public displayedColumns: string[] = ['userId', 'email', 'actions'];
    public selection = new SelectionModel<User>(true);

    private _subscription = new Subscription();

    constructor(
        private _cd: ChangeDetectorRef,
        private _dialog: MatDialog,
    ) { }

    ngAfterViewInit(): void {
        this.dataSource.sort = this.sort;
        this.dataSource.paginator = this.paginator;
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.users) {
            if (changes.users.currentValue !== changes.users.previousValue) {
                this.dataSource.data = changes.users.currentValue;
                this._cd.markForCheck();
            }
        }
    }

    /**
     * Open the user selection dialog & emit `addUser` event with the selected ones on close
     */
    public onClickAdd(): void {
        const dialog = this._dialog.open(SelectUserComponent, {
            width: '400px',
            maxWidth: '90vw',
            minWidth: '25vw',
            data: this.users,
        });
        this.addUserButton?._elementRef.nativeElement.classList.remove('highlight');
        this._subscription.add(
            dialog.afterClosed().subscribe(
                (users: User[]) => {
                    // If cancelled, the users is undefined
                    if (users) {
                        this.addUsers.emit(users);
                    }
                }
            )
        );
    }

    /**
     * Emits the `removeUsers` event with the clicked user
     * @param user The user removed
     */
    public onClickRemove(user: User & {removed?:boolean}): void {
        this.removeUsers.emit([user]);
    }

    /**
     * Emits the `removeUsers` event with the selected users
     */
    public onClickRemoveMass(): void {
        this.removeUsers.emit(this.selection.selected);
    }

    /**
       * Called when the user change the paginator page
       * - Aims to auto scroll to the top of the table
       */
    public scrollUp() {
        const elements = this.tableContainer.nativeElement.children;
        let table: HTMLTableElement;
        for (let i = 0; i < elements.length; i++) {
            if (elements.item(i).tagName === 'TABLE') {
                table = elements.item(i) as HTMLTableElement;
                break;
            }
        }
        table?.scrollIntoView({ behavior: 'smooth' });
        table?.scrollTo(0, 0);
        this.pageChange.emit();
    }

    ngOnDestroy(): void {
        this._subscription.unsubscribe();
    }

}
